import { supabase } from "@/lib/supabaseClient";

export interface Template {
  id: string;
  title: string;
  description: string;
  category: "financial" | "operations" | "development";
  fileType: "Excel" | "PowerPoint" | "Word/PDF";
  file_url: string;
  file_name: string;
  downloads: number;
  lastWeekDownloads: number;
  tags: string[];
  created_at: string;
  updated_at: string;
}

export async function getTemplates(
  search = "",
  category = ""
): Promise<Template[]> {
  let query = supabase
    .from("templates")
    .select("*")
    .order("created_at", { ascending: false });

  if (search) {
    query = query.or(`title.ilike.%${search}%,description.ilike.%${search}%`);
  }

  if (category) {
    query = query.eq("category", category);
  }

  const { data, error } = await query;

  if (error) {
    console.error("Error fetching templates:", error);
    throw error;
  }

  return data.map((item) => ({
    ...item,
    fileType: item.file_type,
    lastWeekDownloads: item.last_week_downloads,
  }));
}

export async function createTemplate(
  templateData: Omit<
    Template,
    "id" | "downloads" | "lastWeekDownloads" | "created_at" | "updated_at"
  >,
  file: File
): Promise<Template> {
  const fileExt = file.name.split(".").pop();
  const fileName = `${Date.now()}.${fileExt}`;
  const filePath = `templates/${fileName}`;

  const { error: uploadError } = await supabase.storage
    .from("templates")
    .upload(filePath, file);

  if (uploadError) {
    console.error("Error uploading file:", uploadError);
    throw uploadError;
  }

  const {
    data: { publicUrl },
  } = supabase.storage.from("templates").getPublicUrl(filePath);

  const { data, error } = await supabase
    .from("templates")
    .insert([
      {
        title: templateData.title,
        description: templateData.description,
        category: templateData.category,
        file_type: templateData.fileType,
        file_url: publicUrl,
        file_name: file.name,
        tags: templateData.tags,
        downloads: 0,
        last_week_downloads: 0,
      },
    ])
    .select()
    .single();

  if (error) {
    console.error("Error creating template:", error);
    throw error;
  }

  return {
    ...data,
    fileType: data.file_type,
    lastWeekDownloads: data.last_week_downloads,
  };
}

export async function updateTemplate(
  id: string,
  templateData: Partial<Template>,
  file?: File
): Promise<Template> {
  const updates: any = {
    title: templateData.title,
    description: templateData.description,
    category: templateData.category,
    file_type: templateData.fileType,
    tags: templateData.tags,
    updated_at: new Date().toISOString(),
  };

  if (file) {
    const fileExt = file.name.split(".").pop();
    const fileName = `${Date.now()}.${fileExt}`;
    const filePath = `templates/${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from("templates")
      .upload(filePath, file);

    if (uploadError) {
      console.error("Error uploading file:", uploadError);
      throw uploadError;
    }

    const {
      data: { publicUrl },
    } = supabase.storage.from("templates").getPublicUrl(filePath);

    updates.file_url = publicUrl;
    updates.file_name = file.name;
  }

  const { data, error } = await supabase
    .from("templates")
    .update(updates)
    .eq("id", id)
    .select()
    .single();

  if (error) {
    console.error("Error updating template:", error);
    throw error;
  }

  return {
    ...data,
    fileType: data.file_type,
    lastWeekDownloads: data.last_week_downloads,
  };
}

export async function deleteTemplate(id: string): Promise<void> {
  const { error } = await supabase.from("templates").delete().eq("id", id);

  if (error) {
    console.error("Error deleting template:", error);
    throw error;
  }
}

export async function recordDownload(
  templateId: string,
  userId?: string,
  userEmail?: string
): Promise<void> {
  await supabase.rpc("increment_downloads", { template_id: templateId });

  await supabase.from("template_downloads").insert([
    {
      template_id: templateId,
      user_id: userId,
      user_email: userEmail,
      downloaded_at: new Date().toISOString(),
    },
  ]);
}

export async function getRecentDownloads(limit = 10) {
  const { data, error } = await supabase
    .from("template_downloads")
    .select(
      `
      id,
      user_email,
      downloaded_at,
      templates:template_id (
        title
      )
    `
    )
    .order("downloaded_at", { ascending: false })
    .limit(limit);

  if (error) {
    console.error("Error fetching recent downloads:", error);
    throw error;
  }

  return data.map((item) => ({
    email: item.user_email,
    date: new Date(item.downloaded_at).toLocaleDateString(),
    template: item.templates.title,
  }));
}
